import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiPackage, FiFilter, FiChevronRight, FiTruck, FiCheckCircle,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import orderService from '../../services/order.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const statusColors = {
  pending: 'warning',
  confirmed: 'info',
  processing: 'info',
  shipped: 'primary',
  delivered: 'success',
  cancelled: 'danger',
  refunded: 'secondary',
};

const nextStatusMap = {
  pending: 'confirmed',
  confirmed: 'processing',
  processing: 'shipped',
  shipped: 'delivered',
};

const nextStatusLabel = {
  pending: 'Confirm Order',
  confirmed: 'Start Processing',
  processing: 'Mark as Shipped',
  shipped: 'Mark as Delivered',
};

const FarmerOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [updating, setUpdating] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await orderService.getMerchantOrders(params);
      setOrders(data.data);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, statusFilter]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    let extra = {};

    // Prompt for tracking info when shipping
    if (newStatus === 'shipped') {
      const trackingNumber = window.prompt('Enter tracking number (optional):');
      if (trackingNumber !== null && trackingNumber.trim()) {
        extra.trackingNumber = trackingNumber.trim();
        const carrier = window.prompt('Shipping carrier (e.g. FedEx, DHL, India Post):');
        if (carrier && carrier.trim()) {
          extra.shippingCarrier = carrier.trim();
        }
      }
    }

    setUpdating(orderId);
    try {
      await orderService.updateStatus(orderId, newStatus, extra);
      toast.success(`Order updated to ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order');
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="section-padding">
      <div className="container">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <h2 className="mb-1">Orders</h2>
            <p className="text-muted mb-0">{pagination.total || 0} total orders</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <FiFilter className="text-muted" />
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : orders.length > 0 ? (
          <>
            <div className="dashboard-card p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Order #</th>
                      <th>Date</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
                      const canAdvance = nextStatusMap[order.status];

                      return (
                        <tr key={order.id}>
                          <td>
                            <Link
                              to={`/account/orders/${order.id}`}
                              className="fw-semibold text-primary text-decoration-none"
                            >
                              {order.orderNumber}
                            </Link>
                            <br />
                            <small className="text-muted">
                              {new Date(order.createdAt).toLocaleDateString()}
                            </small>
                          </td>
                          <td className="text-muted small">
                            {new Date(order.createdAt).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric',
                            })}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              {items?.slice(0, 2).map((item, i) => (
                                <img
                                  key={i}
                                  src={item.image || '/placeholder.png'}
                                  alt=""
                                  className="rounded border"
                                  style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                                />
                              ))}
                              <small className="text-muted ms-1">
                                {items?.length} item{items?.length !== 1 ? 's' : ''}
                              </small>
                            </div>
                          </td>
                          <td className="fw-semibold">${parseFloat(order.totalAmount).toFixed(2)}</td>
                          <td>
                            <span className={`badge ${order.paymentStatus === 'paid' ? 'bg-success' : 'bg-warning'}`}>
                              {order.paymentStatus}
                            </span>
                          </td>
                          <td>
                            <span className={`badge bg-${statusColors[order.status] || 'secondary'}`}>
                              {order.status}
                            </span>
                          </td>
                          <td>
                            {canAdvance && (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleStatusUpdate(order.id, canAdvance)}
                                disabled={updating === order.id}
                              >
                                {updating === order.id ? '...' : (
                                  <>
                                    {canAdvance === 'shipped' ? <FiTruck size={12} className="me-1" /> :
                                     canAdvance === 'delivered' ? <FiCheckCircle size={12} className="me-1" /> : null}
                                    {nextStatusLabel[order.status]}
                                  </>
                                )}
                              </button>
                            )}
                            {!canAdvance && order.status !== 'cancelled' && (
                              <span className="text-success small">
                                <FiCheckCircle className="me-1" /> Complete
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination.pages > 1 && (
              <nav className="d-flex justify-content-center mt-4">
                <ul className="pagination">
                  {Array.from({ length: pagination.pages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => { setPage(i + 1); window.scrollTo(0, 0); }}>
                        {i + 1}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <FiPackage size={64} className="text-muted mb-3" />
            <h4>No orders {statusFilter ? 'matching this filter' : 'yet'}</h4>
            <p className="text-muted">Orders from customers will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmerOrdersPage;
